import { useMsal } from "@azure/msal-react";
import { DirectionalHint, IColumn, IStackProps, Stack, useTheme } from "@fluentui/react";
import { RootState } from "app/store";
import { IGridItem } from "components/controls/CustomDetailList/IGridItem";
import InformationCallout from "components/controls/InformationCallout";
import LookupControlBase from "components/controls/LookupControlBase";
import LookupControlDialog, { TSearchPredicateAsync } from "components/controls/LookupControlDialog";
import saveAs from "file-saver";
import { appStyles, createNonStandard, createNonStandardTemplate, createNonStandardTemplateColumns, emitCustomEvent, fadeIn, friendlyValue } from "global/Application";
import { useApplication } from "global/Application/Context";
import { MsalTokenSource } from "global/DataSources/MsalTokenSource";
import PageLoading from "global/PageLoading";
import { addUpdateNonStandards, addUpdateNonStandardTemplates } from "global/Reducer/dataSlice";
import { ArrayToLookupByPredicate, ArrayToLookupByPredicateWithMap, formatDate, parseDate, timestamp } from "global/Utility/TSFunctions";
import {
    bcbs_config_category as IXrmTypes_bcbs_config_category,
    bcbs_config_section as IXrmTypes_bcbs_config_section,
    bcbs_groupconfigurationsnapshot as IXrmTypes_bcbs_groupconfigurationsnapshot
} from "model/CRM";
import {
    IAccountsReceivable as FAM_IAccountsReceivable,
    IBenefit as FAM_IBenefit,
    IFeesAndPremiums as FAM_IFeesAndPremiums,
    IGroupConfigurationData as FAM_IGroupConfigurationData,
    INonStandard as FAM_INonStandard,
    INonStandardTemplate as FAM_INonStandardTemplate,
    IObjectWithNameId as FAM_IObjectWithNameId,
    IStopLoss as FAM_IStopLoss
} from "model/FlexibleAttributeModel";
import { getEntityContext, IEntityContext } from 'global/Utility/EntityHelpers';
import { useEffect, useMemo, useState } from "react";
import { connect, ConnectedProps, useDispatch } from "react-redux";
import { ITierLevelChange } from "views/TierWizard";
import { ConfirmationDialog } from "../../components/controls/ConfirmationDialog";
import { EControlType } from "../../components/ControlWrapper";
import { AppDataSource } from "../../global/DataSources/AppDataSource";
import { setProcessing } from "../../global/Reducer/statusSlice";
import { EVariant } from "./NonStandardEditor";
import NonStandardTemplateEditor from "./NonStandardTemplateEditor";
import { ESaveType, moveTemplatedNonStandardsToNewSectionAction, newNonStandardFromTemplateAction, SaveAsync, TemplateToBenefitOrGCAction } from "global/Application/dataActions";
import { $att_$non_standard_$bcbs_benefitdefinition_id } from "model/ConfigAttributeConstants";

interface IProps extends TComponentConnectedProps {
    //TODO: unmount component with delay for animation
    isOpen: boolean;

    sectionForNS?: IXrmTypes_bcbs_config_section;

    benefit?: FAM_IBenefit;
    groupConfiguration?: FAM_IGroupConfigurationData;
    accountsReceivable?: FAM_IAccountsReceivable;
    feesAndPremium?: FAM_IFeesAndPremiums;
    stopLoss?: FAM_IStopLoss;

    onDismiss: () => void
}

enum ENonstandardScope {
    GroupConfiguration,
    Benefit
}

enum ENonstandardTemplateScope {
    GroupConfiguration,
}


interface IScopeSearchResult extends FAM_IObjectWithNameId {
    groupConfiguration: FAM_IGroupConfigurationData | null;
    benefit: FAM_IBenefit | null;
}


interface INonstandardTemplateSearchResult extends FAM_IObjectWithNameId {
    item: FAM_INonStandardTemplate;
}

interface INonstandardScope {
    nonStandard: FAM_INonStandard;
    parent: FAM_IBenefit | FAM_IGroupConfigurationData;
    type: ENonstandardScope;
}

interface INonstandardTemplateScope {
    nonStandardTemplate: FAM_INonStandardTemplate;
    parent: FAM_IGroupConfigurationData;
    type: ENonstandardTemplateScope;
}

interface IBenefitNonstandardScope extends INonstandardScope {
    parent: FAM_IBenefit
    type: ENonstandardScope.Benefit;
}

interface IImportNonstandardContext {
    snapshot?: IXrmTypes_bcbs_groupconfigurationsnapshot | null;
    json?: FAM_IGroupConfigurationData | null;
    nonStandardSearchResult?: INonstandardScope[] | null;
    nonStandardTemplateSearchResult?: INonstandardTemplateScope[] | null;
    scopeSearchResult?: IScopeSearchResult[] | null;
    searching?: boolean;
}

const NonStandardTemplateMover = (props: IProps) => {
    // current view state/stages
    //const [nonStandardTemplate, setNonStandardTemplate] = useState<FAM.INonStandardTemplate>(props.nonStandardTemplate);

    const [linkedNSCategoryType, setLinkedNSCategoryType] = useState<IXrmTypes_bcbs_config_category | null>(null);
    const [entityContext, setEntityContext] = useState<IEntityContext | null>(null);
    const [alertIsMounted, setAlertIsMounted] = useState(false);
    const [alertIsOpen, _setAlertIsOpen] = useState(false);
    const setAlertIsOpen: React.Dispatch<React.SetStateAction<boolean>> = (value) => {
        if (!alertIsMounted)
            setAlertIsMounted(true);
        _setAlertIsOpen(value);
    }

    const [sourceContext, setSourceContext] = useState<IImportNonstandardContext | null>({
        nonStandardSearchResult: null,
        scopeSearchResult: null,
        snapshot: props.state.data.snapshot,
        json: props.state.data.json,
    });

    const state = props.state;
    const { instance } = useMsal();
    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);

    const categoryNonStandardTemplate = props.state.data.config.cache.map_bcbs_config_category_named['Non-Standard Template'];

    const dispatch = useDispatch();

    useEffect(() => {
        if (props.isOpen) {
            setSourceContext({
                snapshot: props.state.data.snapshot,
                json: props.state.data.json,
                scopeSearchResult: null,
            });
            // Use EntityHelpers to get entity context
            const context = getEntityContext({
                data: props.state.data,
                benefit: props.benefit,
                groupConfiguration: props.groupConfiguration,
                accountsReceivable: props.accountsReceivable,
                feesAndPremium: props.feesAndPremium,
                stopLoss: props.stopLoss,
                sectionForNSConfigId: props.sectionForNS?._bcbs_config_categoryid_value
            });
            setEntityContext(context);

            // Use entity context if available, otherwise fall back to original logic
            if (context?.categoryConfigId) {
                let configCategory = props.state.data.config.cache.map_bcbs_config_category[context.categoryConfigId];
                setLinkedNSCategoryType(configCategory);
            }
            
        }
    }, []);

    /**
     * Renders an information callout with audit data.
     * @param auditData - The audit data to display in the callout.
     * @returns The rendered InformationCallout component.
     */
    const onRenderInformationCallout = (auditData?: IGridItem<INonstandardTemplateSearchResult>) => {

        if (!auditData?.data?.item)
            return <></>;

        const nonStandardTemplate = auditData?.data?.item;

        return <InformationCallout
            hover
            postText={auditData.compareKeys}
            directionalHint={DirectionalHint.rightCenter}
            calloutClassName={appStyles.calloutView}
        >
            <div style={{ margin: '10px' }}>
                <NonStandardTemplateEditor
                    variant={EVariant.viewer}
                    isOpen={true}
                    disabled={true}
                    defaultValue={nonStandardTemplate ?? null}
                    openViewer={() => { }}
                />

            </div>
        </InformationCallout>;
    }

    /**
     * Search for all records applicable to selected scope
     * @param value 
     * @returns 
     */
    const onSearchNonStandardTemplates: TSearchPredicateAsync<INonstandardTemplateSearchResult> = async (value) => {
        const searchValue = value?.toLowerCase() ?? '';

        return new Promise((resolve, reject) => {

            const items: IGridItem<INonstandardTemplateSearchResult>[] = [];

            // Get the current entity ID and linked category name based on entity context
            const currentEntityId = entityContext?.entityId;
            const linkedCategoryName = linkedNSCategoryType?.bcbs_name;

            props.state.data.json.nonStandardTemplates?.forEach((nonStandardTemplateItem, ix) => {

                let add = false;

                if (!add && !searchValue)
                    add = true;

                if (!add && nonStandardTemplateItem.name.toLowerCase().indexOf(searchValue) > -1)
                    add = true;

                if (nonStandardTemplateItem.deactivated === true) {
                    add = false
                }

                let relationshipExists = false;
                //if template is linked to this entity, not in this section, and only has one section in each relationship
                //find if entity we are on currently is in template
                const entityRelationship = nonStandardTemplateItem?.relationships?.find(rel => rel.categoryID === currentEntityId);
                //if we have an entity relationship - show it
                if(entityRelationship)
                {
                    //if that entity relationship has section that includes the section we are choosing - dont show it
                    if(entityRelationship.sectionRefs?.find(sectionRef => sectionRef.sectionId === props.sectionForNS?.bcbs_config_sectionid))
                    {
                        relationshipExists = true;
                    }
                    else if(entityRelationship.sectionRefs?.length !== undefined && entityRelationship.sectionRefs?.length > 1)
                    {
                        //if we have more than one section in entity dont show it
                        relationshipExists = true;
                    }
                }else{
                    relationshipExists = true;
                }
                
                if ((add && nonStandardTemplateItem?.linkedCategoryName === linkedCategoryName) && !relationshipExists && nonStandardTemplateItem.deactivated === false) {

                    const item: IGridItem<INonstandardTemplateSearchResult> = {
                        key: ix,
                        _index: ix,
                        name: nonStandardTemplateItem.name,
                        scope: "",
                        data: {
                            name: nonStandardTemplateItem.name,
                            id: nonStandardTemplateItem.id,
                            item: nonStandardTemplateItem,
                        }
                    };


                    nonStandardTemplateItem.fields.forEach(eaField => {
                        const configAttributeId = eaField.configRef.id;
                        eaField.values.forEach(eachFieldValue => {
                            item[`${configAttributeId}-${eachFieldValue?.tierRef?.id ?? ''}`] = eachFieldValue.value;
                        });
                    });

                    items.push(item);

                }
            })

            const quickViewColumn: IColumn = {
                key: 'view',
                minWidth: 50,
                name: 'View',
                fieldName: 'view',
                isResizable: true,
                onRender(item: IGridItem<INonstandardTemplateSearchResult>, index, column) {
                    return onRenderInformationCallout(item)
                },
            };

            const categoryNonStandardTemplate = props.state.data.config.cache.map_bcbs_config_category_named['Non-Standard Template'];

            // generating a temporary non-standard to match the schema
            const columns = createNonStandardTemplateColumns(
                props.state.data.config,
                {
                    category: categoryNonStandardTemplate,
                    linkedCategory: null,
                    config: props.state.data.config,
                },
                undefined,
                undefined,
                (field, configAttribute, configMasterAttribute) => {

                    // https://bcbsonline.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=ff72f3f2-d38d-ec11-b400-0022480a9a56
                    if (configAttribute?.bcbs_config_attributeid === $att_$non_standard_$bcbs_benefitdefinition_id)
                        return true;

                    return false;
                }
            );


            resolve({
                columns: [
                    {
                        key: 'name',
                        minWidth: 350,
                        name: 'Name',
                        fieldName: 'name',
                        isResizable: true,
                    },
                    ...columns,
                    quickViewColumn,
                ],
                items: items,
                showNoResultPrompt: items.length === 0,
                showLimitedResultPrompt: false,

            })
        });
    }

    function onRenderBody(): JSX.Element {
        if (!entityContext) return <></>;
        return <div className={fadeIn}>
            {onRenderValidationDialog()}
            <Stack tokens={{ childrenGap: 10 }}>

                {sourceContext?.searching &&
                    <PageLoading />
                }

                <LookupControlBase
                    label='Entity'
                    value={sourceContext?.scopeSearchResult?.[0]?.name}
                    disabled
                    onLookup={() => {
                        setSourceContext(q => {
                            return {
                                json: null,
                                nonStandardSearchResult: null,
                                scopeSearchResult: null,
                                snapshot: null
                            }
                        });
                    }}
                />

                <LookupControlDialog
                    isOpen={sourceContext?.snapshot !== null &&
                        sourceContext?.json !== null &&
                        sourceContext?.scopeSearchResult === null}
                    label={'Non-Standard Templates'}
                    variant="multiple"
                    onSearch={onSearchNonStandardTemplates}
                    onPick={(picks) => {
                        const nonStandardTemplates: FAM_INonStandardTemplate[] = [];
                        
                        const chosenTemplates = picks?.map(q => q.item);
                        if (chosenTemplates && props.sectionForNS)
                        {
                            dispatch(moveTemplatedNonStandardsToNewSectionAction(chosenTemplates, entityContext, {
                                benefit: props.benefit, 
                                section: props.sectionForNS, 
                                groupConfiguration: props.groupConfiguration,
                                stopLoss: props.stopLoss
                            }))
                        }
                        setAlertIsOpen(true);
                        //props.onDismiss?.();
                    }}
                    onCancel={() => {
                        setSourceContext(q => {
                            return {
                                ...q,
                                nonStandardSearchResult: null,
                                scopeSearchResult: null,
                            }
                        });
                        props.onDismiss?.();
                    }}
                    onDismiss={() => {
                        setSourceContext(q => {
                            return {
                                ...q,
                                nonStandardSearchResult: null,
                                scopeSearchResult: null,
                            }
                        });
                        props.onDismiss?.();
                    }}
                />

            </Stack>
        </div>
    }

    const onRenderValidationDialog = () => {
        if (!alertIsMounted)
            return <></>;

        return <ConfirmationDialog
            onContinue={() => {
                setAlertIsOpen(false); setAlertIsMounted(false);props.onDismiss?.();
                setTimeout(()=>{
                    SaveAsync(
                        dispatch,
                        dataSource,
                        props.state,
                        {saveType: ESaveType.EAuto});
                },100)
            }}
            onClose={() => { setAlertIsOpen(false); setAlertIsMounted(false); props.onDismiss?.(); }}
            continueText="Yes"
            cancelText="No"
        >
            <p>This will move every Non-Standard on this template to this section for each benefit this template Applies to. </p>
            <p>You can open then NSE extract to view all Non-Standards and their changes.</p>
            <p>Do you wish to continue?</p>
        </ConfirmationDialog>;
    }

    

    return onRenderBody()

}

function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(NonStandardTemplateMover);



